import * as React from 'react';
import { Link, NavLink } from 'react-router-dom';
import { HasPermission } from '../../containers/authorization-helpers/HasPermission';
import { HasServiceCenters } from '../../containers/authorization-helpers/HasServiceCenter';
import { INavbarGroupLinkProps } from './Navbar';
import { Routes } from '../../utils/app-constants';
import { NavbarGroupHeader } from './NavbarGroupHeader';

// #region Component Props

interface IProps  {
    title           : string;
    icon            : string;
    links           : INavbarGroupLinkProps[];

    // Open in the sidebar, with its links under it
    isOpen          : boolean;

    // Open, and kept open: the sidebar has few enough sections to show all of them
    isFixed         : boolean;

    // The page is one of its links
    isCurrent       : boolean;

    // With the sidebar collapsed to icons: open next to it, where it was clicked
    isFlyout        : boolean;
    flyoutTop?      : number;
    flyoutLeft?     : number;

    onHeaderClick   : (title : string, header : HTMLElement) => void;

    // The pointer comes onto the section, or leaves it and its flyout
    onHoverStart    : (title : string, header : HTMLElement) => void;
    onHoverEnd      : () => void;
}

// #endregion

// Keeps the flyout clear of the bottom of the window, and of the person's card that sits over the bottom of the sidebar
const FLYOUT_MARGIN = 8;
const ACCOUNT_CARD_HEIGHT = 64;

export const NavbarGroup = (props: IProps) => {

    const linksRef = React.useRef<HTMLDivElement>(null);
    const linksId = 'navbar-' + props.title.toLowerCase().replace(/[^a-z0-9]+/g, '-');

    React.useLayoutEffect(() => {

        const panel = linksRef.current;
        if (!panel || !props.isFlyout || props.flyoutTop === undefined) {
            return;
        }

        const lowest = window.innerHeight - ACCOUNT_CARD_HEIGHT - FLYOUT_MARGIN - panel.offsetHeight;
        panel.style.top = Math.max(FLYOUT_MARGIN, Math.min(props.flyoutTop, lowest)) + 'px';

    }, [props.isFlyout, props.flyoutTop]);

    const classes = [
        'app-module',
        props.isOpen ? 'open' : '',
        props.isFixed ? 'fixed' : '',
        props.isCurrent ? 'current' : '',
        props.isFlyout ? 'flyout' : ''
    ].filter((x : string) => x !== '').join(' ');

    const onMouseEnter = (e : React.MouseEvent<HTMLDivElement>) => {
        const header = e.currentTarget.querySelector('h3 button') as HTMLElement | null;
        if (header) {
            props.onHoverStart(props.title, header);
        }
    };

    return <div className={ classes } onMouseEnter={ onMouseEnter } onMouseLeave={ () => props.onHoverEnd() }>
        <NavbarGroupHeader
            icon={ props.icon }
            title={ props.title }
            isExpanded={ props.isOpen || props.isFlyout }
            isFixed={ props.isFixed }
            controls={ linksId }
            onClick={ (header : HTMLElement) => props.onHeaderClick(props.title, header) } />
        {
            props.links.length > 0
                ? <div
                    id={ linksId }
                    ref={ linksRef }
                    className="module-links"
                    style={ props.isFlyout ? { top: props.flyoutTop, left: props.flyoutLeft } : undefined }>
                    <div className="module-links-inner">
                        <div className="module-flyout-title">
                            { props.icon ? <i className={ 'la ' + props.icon } /> : null }
                            { props.title }
                        </div>
                        <ul>
                            {
                                props.links.map((subroute : INavbarGroupLinkProps, idx : number) => (
                                    <HasServiceCenters
                                        key={`navlinkitm-${idx}`}
                                        requiredServiceCenters={ subroute.requiredServiceCenters }>
                                        <HasPermission requiredPermissions={ subroute.permissions }>
                                            <li>
                                                {
                                                    subroute.path === Routes.UTILITY_NOT_READY
                                                        ? <Link to={subroute.path} className="nav-item">
                                                            {subroute.title}
                                                        </Link>
                                                        : <NavLink to={subroute.path} className="nav-item">
                                                            {subroute.title}
                                                        </NavLink>
                                                }
                                            </li>
                                        </HasPermission>
                                    </HasServiceCenters>
                                ))
                            }
                        </ul>
                    </div>
                </div>
                : null
        }
    </div>;
};
